'use server'

import { apiRequest, ApiResponse } from '../request'

/** Um número da conta, como a Meta o reporta. */
export interface WhatsappPhoneNumber {
    id: string
    displayPhoneNumber: string
    verifiedName?: string
    qualityRating?: string
    /** `CONNECTED` já envia; `PENDING` ainda precisa ser registrado. */
    status?: string
    codeVerificationStatus?: string
}

export interface WhatsappAccountStatusResult {
    connected: boolean
    wabaId?: string
    status?: string
    lastError?: string | null
    updatedAt?: string
    phoneNumbers?: WhatsappPhoneNumber[]
    /**
     * PIN de registro de cada número (`phoneNumberId` -> PIN), em claro.
     *
     * Exibido de propósito: é a verificação em duas etapas do número na Meta, e
     * um cliente que não conhece o próprio código fica preso ao fornecedor que
     * o gerou.
     */
    phonePins?: Record<string, string>
}

/** O que o front precisa para abrir o popup do Embedded Signup. */
export interface WhatsappSignupConfig {
    configId: string
    appId: string
    graphVersion: string
}

export interface WhatsappConnectResult {
    success: boolean
    wabaId: string
    /** Falso significa conta conectada que NÃO recebe webhook — precisa reinscrever. */
    subscribed: boolean
    phoneNumbers: WhatsappPhoneNumber[]
    /** Números que o próprio onboarding registrou, sem pedir nada ao cliente. */
    registeredPhoneNumbers: string[]
}

export async function getWhatsappSignupConfig(): Promise<
    ApiResponse<WhatsappSignupConfig>
> {
    return apiRequest<WhatsappSignupConfig>(
        'messaging/whatsapp-account/signup-config',
        {},
    )
}

export async function getWhatsappAccountStatus(): Promise<
    ApiResponse<WhatsappAccountStatusResult>
> {
    return apiRequest<WhatsappAccountStatusResult>('messaging/whatsapp-account', {})
}

/**
 * Conclui a conexão. O `code` vem do popup e é trocado por token NO SERVIDOR —
 * o token da conta do cliente nunca passa pelo navegador.
 */
export async function connectWhatsappAccount(payload: {
    code: string
    wabaId?: string
    phoneNumberId?: string
}): Promise<ApiResponse<WhatsappConnectResult>> {
    return apiRequest<WhatsappConnectResult>(
        'messaging/whatsapp-account/connect',
        { data: payload },
        'post',
    )
}

/** Habilita o ENVIO por um número. O PIN vira a verificação em duas etapas dele. */
export async function registerWhatsappPhoneNumber(
    phoneNumberId: string,
    pin: string,
): Promise<ApiResponse<{ success: boolean; error?: string }>> {
    return apiRequest<{ success: boolean; error?: string }>(
        `messaging/whatsapp-account/phone-numbers/${phoneNumberId}/register`,
        { data: { pin } },
        'post',
    )
}

/**
 * Desconecta a conta: remove a inscrição do nosso app na Meta e apaga o token.
 * O cliente volta a ter controle total da conta dele.
 */
export async function disconnectWhatsappAccount(): Promise<
    ApiResponse<{ success: boolean; unsubscribed: boolean }>
> {
    return apiRequest<{ success: boolean; unsubscribed: boolean }>(
        'messaging/whatsapp-account',
        {},
        'delete',
    )
}

/** Vínculo do setor com o número da API oficial. */
export interface DepartmentPhoneNumberStatus {
    provider: 'WHATSAPP_API' | 'WHATSAPP_WEB'
    whatsappNumber: string | null
    linked: boolean
    phoneNumberId: string | null
    displayPhoneNumber: string | null
    verifiedName: string | null
    accountConnected: boolean
}

export interface DepartmentPhoneNumberLinkResult extends DepartmentPhoneNumberStatus {
    outcome: 'LINKED' | 'NO_NUMBER' | 'NO_ACCOUNT' | 'NOT_FOUND' | 'NOT_API'
    availableNumbers: string[]
}

export async function getDepartmentPhoneNumberStatus(
    departmentId: number
): Promise<ApiResponse<DepartmentPhoneNumberStatus>> {
    return apiRequest<DepartmentPhoneNumberStatus>(`messaging/whatsapp-account/departments/${departmentId}/phone-number`, {})
}

export async function linkDepartmentPhoneNumber(
    departmentId: number
): Promise<ApiResponse<DepartmentPhoneNumberLinkResult>> {
    return apiRequest<DepartmentPhoneNumberLinkResult>(
        `messaging/whatsapp-account/departments/${departmentId}/phone-number/link`,
        {},
        'post'
    )
}
